import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import useForm from 'react-hook-form';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Switch } from 'pretty-checkbox-react';
/* actions */
import {
  getAllPermissionsGroup,
  editPermissionsGroup
} from '../../redux/actions/permissionsGroup';
import { getAllCompanies } from '../../redux/actions/companies';
/* components */
import ErrorMsgField from '../../components/ErrorMsgField';
import AlertMsg from '../../components/AlertMsg';
import FormFooter from '../../components/FormFooter';
import FormHeader from '../../components/FormHeader';
/* form-validation */
import schema from '../../form-validation/permissionsGroup';
/* utils */
import {
  customStyle,
  customThemes,
  customFilter,
  firstOption
} from '../../utils/select';
import sortable from '../../utils/sortable';
import handleChangeDefault from '../../utils/handleChangeDefault';
import {
  idPermissionsGroup4Case,
  idCompany4Case
} from '../../utils/defaultPermissions';
import hideItems from './hideItems';

function Edit (props) {
  const { id } = props.match.params;
  const INITIAL_VALUES = {
    _id: id || '',
    name: '',
    description: '',
    company: '',
    permissions: []
  };
  const dispatch = useDispatch();
  const submitButton = useRef();
  const { register, handleSubmit, errors } = useForm({
    validationSchema: schema,
    reValidateMode: 'onSubmit'
  });
  const permissionsGroup = useSelector(state => state.permissionsGroup);
  const permissionsGrp = permissionsGroup.data.find(item => item._id === id);
  const [data, setData] = useState(INITIAL_VALUES);
  const companies = useSelector(state => state.companies.data);
  const [companyOptions, setSpecieOptions] = useState([firstOption]);
  const sessionUser = useSelector(state => state.authentications.user);

  useEffect(() => {
    const { permissionsGroup } = sessionUser;
    const query =
      permissionsGroup._id && permissionsGroup._id === idPermissionsGroup4Case
        ? {}
        : { _id: permissionsGroup.company };
    dispatch(getAllCompanies(query));
    dispatch(getAllPermissionsGroup(query._id ? { company: query._id } : {}));
  }, [dispatch]);

  useEffect(() => {
    if (permissionsGrp) {
      const { company } = permissionsGrp;
      const corpGuides = company.permissions.reduce((array, guide) => {
        if (guide.enabled) {
          array.push(guide.id);
        }
        return array;
      }, []);
      const corpMenus = company.permissions.reduce((array, guide) => {
        if (guide.enabled) {
          if (guide.items) {
            const menus = guide.items.reduce((array, menu) => {
              if (menu.enabled) {
                array.push(menu.id);
              }
              return array;
            }, []);
            return array.concat(menus);
          }
        }
        return array;
      }, []);
      const corpSubMenus = company.permissions.reduce((array, guide) => {
        if (guide.enabled) {
          if (guide.items) {
            const menus = guide.items.reduce((array, menu) => {
              if (menu.enabled) {
                if (menu.items) {
                  const submenus = menu.items.reduce((array, submenu) => {
                    if (submenu.enabled) {
                      array.push(submenu.id);
                    }
                    return array;
                  }, []);
                  return array.concat(submenus);
                }
              }
              return array;
            }, []);
            return array.concat(menus);
          }
        }
        return array;
      }, []);
      const { permissions } = permissionsGrp;
      const currentPermissions = permissions.reduce((array, guide) => {
        if (corpGuides.includes(guide.id)) {
          let items = [];
          if (guide.items) {
            items = guide.items.reduce((array, menu) => {
              if (corpMenus.includes(menu.id)) {
                let items = [];
                if (menu.items) {
                  items = menu.items.reduce((array, submenu) => {
                    if (corpSubMenus.includes(submenu.id)) {
                      array.push(submenu);
                    }
                    return array;
                  }, []);
                }
                array.push({ ...menu, items });
              }
              return array;
            }, []);
          }
          array.push({ ...guide, items });
        }
        return array;
      }, []);
      const newPermissions = [...currentPermissions];
      company.permissions.forEach(guide => {
        const item = currentPermissions.find(item => item.id === guide.id);
        if (!item && guide.enabled) {
          newPermissions.push({ ...guide, enabled: false });
        }
      });
      company.permissions.forEach(guide => {
        const item = newPermissions.find(item => item.id === guide.id);
        if (item && item.items && guide.items) {
          const menus = guide.items.reduce((array, menu) => {
            const currentMenu = item.items.find(item => item.id === menu.id);
            const obj =
              menu.enabled && !currentMenu
                ? { ...menu, enabled: false }
                : currentMenu;
            if (obj) {
              array.push(obj);
            }
            return array;
          }, []);
          item.items = [...menus];
        }
      });
      company.permissions.forEach(guide => {
        const item = newPermissions.find(item => item.id === guide.id);
        if (item && item.items && guide.items) {
          guide.items.forEach(menu => {
            let items = [];
            if (menu.items && menu.enabled) {
              const oldMenu = item.items.find(item => item.id === menu.id);
              const currentMenu = menu.items.filter(item => item.enabled);
              items = currentMenu
                .map(menu => {
                  const item = oldMenu.items.find(item => item.id === menu.id);
                  return item || { ...menu, enabled: false };
                })
                .filter(
                  submenu =>
                    !(
                      submenu.id === 'companies' &&
                      sessionUser.permissionsGroup._id !==
                        idPermissionsGroup4Case
                    )
                );
              const submenus = items.map(submenu => {
                /*
                c: criar
                l: listar
                u: atualizar
                d: deletar
                r: restaurar
                h: ocultar dados sensiveis
                */
                const options = submenu.options
                  ? { ...submenu.options }
                  : {
                    c: false,
                    l: false,
                    u: false,
                    d: false,
                    r: false,
                    h: false
                  };
                return {
                  ...submenu,
                  options
                };
              });
              oldMenu.items = [...submenus];
            }
          });
        }
      });
      const newData = {
        ...permissionsGrp,
        company: permissionsGrp.company ? permissionsGrp.company._id : '',
        permissions: newPermissions
      };
      setData(newData);
    }
  }, [permissionsGrp]);

  useEffect(() => {
    if (companies.length) {
      const options = companies
        .map(company => ({
          value: company._id,
          label: `${company.name}`,
          sortValue: `${company.name}`
        }))
        .sort(sortable);

      options.unshift(firstOption);
      setSpecieOptions([...options]);
    }
  }, [companies, setSpecieOptions]);

  const handleChangeGuides = evt => {
    const { name, checked } = evt.target;
    const permissions = data.permissions.map(guide => {
      if (guide.id === name) {
        return { ...guide, enabled: checked };
      }
      return { ...guide };
    });
    setData({ ...data, permissions });
  };

  const handleChangeMenu = (evt, guide) => {
    const { name, checked } = evt.target;
    const permissions = data.permissions.map(_guide => {
      if (_guide.id === guide.id && guide.items && guide.enabled) {
        _guide.items = guide.items.map(menu => {
          if (menu.id === name) {
            return { ...menu, enabled: checked };
          }
          return { ...menu };
        });
      }
      return { ..._guide };
    });
    setData({ ...data, permissions });
  };

  const handleChangeSubMenu = (evt, guide, menu) => {
    const { name, checked } = evt.target;
    const permissions = data.permissions.map(_guide => {
      if (_guide.id === guide.id && guide.items && guide.enabled) {
        _guide.items = guide.items.map(_menu => {
          if (_menu.id === menu.id && menu.items && menu.enabled) {
            _menu.items = menu.items.map(submenu => {
              if (submenu.id === name) {
                return { ...submenu, enabled: checked };
              }
              return { ...submenu };
            });
          }
          return { ..._menu };
        });
      }
      return { ..._guide };
    });
    setData({ ...data, permissions });
  };

  const handleChangeSubMenuOptions = (evt, guide, menu, nameSubMenu) => {
    const { name, checked } = evt.target;
    const permissions = data.permissions.map(_guide => {
      if (_guide.id === guide.id && guide.items && guide.enabled) {
        _guide.items = guide.items.map(_menu => {
          if (_menu.id === menu.id && menu.items && menu.enabled) {
            _menu.items = menu.items.map(submenu => {
              if (submenu.id === nameSubMenu) {
                const options = {
                  ...submenu.options,
                  [name]: checked
                };
                return { ...submenu, options };
              }
              return { ...submenu };
            });
          }
          return { ..._menu };
        });
      }
      return { ..._guide };
    });
    setData({ ...data, permissions });
  };

  const submit = () => {
    dispatch(editPermissionsGroup(data, submitButton.current));
  };

  return (
    <div className="container-fluid">
      {permissionsGroup.editSuccess && (
        <AlertMsg
          type="success"
          msg="Grupo de permissões atualizada com sucesso"
        />
      )}
      {permissionsGroup.errorContent &&
        !permissionsGroup.errorContent.message.includes('400') &&
        permissionsGroup.editError && (
        <AlertMsg
          type="danger"
          msg="Falha durante atualização do grupo de permissões"
        />
      )}
      {permissionsGroup.errorContent &&
        permissionsGroup.errorContent.message.includes('400') &&
        permissionsGroup.editError && (
        <AlertMsg
          type="danger"
          msg="Um grupo de permissões com o nome informado já está cadastrado"
        />
      )}
      {!permissionsGroup.editSuccess && !permissionsGroup.editError && (
        <AlertMsg type="info" msg="Área atualização do grupo de permissões" />
      )}
      <FormHeader title="Atualizar Grupo de Permissões" />
      <form onSubmit={handleSubmit(submit)}>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="name">
                Nome<span className="field-required">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                id="name"
                name="name"
                autoComplete="off"
                value={data.name}
                onChange={evt => handleChangeDefault(evt, data, setData)}
                ref={register}
              />
              {errors.name && errors.name.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="company">
                Empresa<span className="field-required">*</span>
              </label>
              <Select
                className="col p-0"
                name="company"
                ref={register}
                isDisabled={true}
                value={companyOptions.find(
                  company => company.value === data.company
                )}
                options={companyOptions}
                isSearchable={true}
                theme={customThemes}
                styles={customStyle}
                filterOption={customFilter}
                noOptionsMessage={() => 'Nenhum registro encontrado'}
              />
              {errors.company && errors.company.type === 'required' && (
                <ErrorMsgField type="required" />
              )}
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="description">
            Descrição<span className="field-required">*</span>
          </label>
          <textarea
            className="form-control"
            id="description"
            name="description"
            rows="3"
            value={data.description}
            onChange={evt => handleChangeDefault(evt, data, setData)}
            ref={register}
          ></textarea>
          {errors.description && errors.description.type === 'required' && (
            <ErrorMsgField type="required" />
          )}
        </div>
        <div className="form-group">
          <label htmlFor="permissions">
            Permissões do Sistema<span className="field-required">*</span>
          </label>
          {data.permissions.length <= 0 && (
            <p>- Nenhuma empresa seleciona até o momento</p>
          )}
          {data.permissions.map((guide, i) => {
            return (
              <div key={i} className="form-group pl-3">
                <div className="checkbox">
                  <Switch
                    type="checkbox"
                    color="success"
                    name={guide.id}
                    onChange={handleChangeGuides}
                    checked={guide.enabled}
                  ></Switch>
                  <span className="ml-2">
                    <i className={guide.icon} /> GUIA {guide.tab}
                  </span>
                </div>
                {guide.items &&
                  guide.items.map((menu, i) => {
                    return (
                      <div key={i} className="mb-1 pl-5">
                        <div className="checkbox">
                          <Switch
                            type="checkbox"
                            color="success"
                            name={menu.id}
                            onChange={evt => handleChangeMenu(evt, guide)}
                            checked={guide.enabled ? menu.enabled : false}
                          ></Switch>
                          <span className="ml-2">
                            <i className={menu.icon} /> {menu.title}
                          </span>
                        </div>
                        {menu.items &&
                          menu.items.map((submenu, i) => {
                            if (
                              submenu.id === 'companies' &&
                              data.company !== idCompany4Case
                            ) {
                              return false;
                            }
                            return (
                              <div key={i} className="mb-1 pl-4">
                                <div className="checkbox">
                                  <Switch
                                    type="checkbox"
                                    color="primary"
                                    name={submenu.id}
                                    onChange={evt =>
                                      handleChangeSubMenu(evt, guide, menu)
                                    }
                                    checked={
                                      guide.enabled && menu.enabled
                                        ? submenu.enabled
                                        : false
                                    }
                                  ></Switch>
                                  <span className="ml-2">
                                    <i className={submenu.icon} />{' '}
                                    {submenu.title}
                                  </span>
                                </div>
                                {/* start options */}
                                {!hideItems.includes(submenu.id) && (
                                  <div className="ml-3">
                                    <Switch
                                      id={submenu.id}
                                      type="checkbox"
                                      color="info"
                                      name="c"
                                      onChange={evt =>
                                        handleChangeSubMenuOptions(
                                          evt,
                                          guide,
                                          menu,
                                          submenu.id
                                        )
                                      }
                                      checked={
                                        guide.enabled &&
                                        menu.enabled &&
                                        submenu.enabled &&
                                        submenu.options.c
                                      }
                                    >
                                      Criar
                                    </Switch>
                                    <Switch
                                      type="checkbox"
                                      color="info"
                                      name="l"
                                      onChange={evt =>
                                        handleChangeSubMenuOptions(
                                          evt,
                                          guide,
                                          menu,
                                          submenu.id
                                        )
                                      }
                                      checked={
                                        guide.enabled &&
                                        menu.enabled &&
                                        submenu.enabled &&
                                        submenu.options.l
                                      }
                                    >
                                      Listar
                                    </Switch>
                                    <Switch
                                      type="checkbox"
                                      color="info"
                                      name="u"
                                      onChange={evt =>
                                        handleChangeSubMenuOptions(
                                          evt,
                                          guide,
                                          menu,
                                          submenu.id
                                        )
                                      }
                                      checked={
                                        guide.enabled &&
                                        menu.enabled &&
                                        submenu.enabled &&
                                        submenu.options.l &&
                                        submenu.options.u
                                      }
                                    >
                                      Atualizar
                                    </Switch>
                                    <Switch
                                      type="checkbox"
                                      color="info"
                                      name="d"
                                      onChange={evt =>
                                        handleChangeSubMenuOptions(
                                          evt,
                                          guide,
                                          menu,
                                          submenu.id
                                        )
                                      }
                                      checked={
                                        guide.enabled &&
                                        menu.enabled &&
                                        submenu.enabled &&
                                        submenu.options.l &&
                                        submenu.options.d
                                      }
                                    >
                                      Deletar
                                    </Switch>
                                    <Switch
                                      type="checkbox"
                                      color="info"
                                      name="r"
                                      onChange={evt =>
                                        handleChangeSubMenuOptions(
                                          evt,
                                          guide,
                                          menu,
                                          submenu.id
                                        )
                                      }
                                      checked={
                                        guide.enabled &&
                                        menu.enabled &&
                                        submenu.enabled &&
                                        submenu.options.l &&
                                        submenu.options.r
                                      }
                                    >
                                      Restaurar
                                    </Switch>
                                    <Switch
                                      type="checkbox"
                                      color="info"
                                      name="h"
                                      onChange={evt =>
                                        handleChangeSubMenuOptions(
                                          evt,
                                          guide,
                                          menu,
                                          submenu.id
                                        )
                                      }
                                      checked={
                                        guide.enabled &&
                                        menu.enabled &&
                                        submenu.enabled &&
                                        submenu.options.h
                                      }
                                    >
                                      Ocultar Dados Sensíveis
                                    </Switch>
                                  </div>
                                )}
                                {/* end options */}
                              </div>
                            );
                          })}
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </div>
        <FormFooter url="/permissions_group" reference={submitButton} />
      </form>
    </div>
  );
}

Edit.propTypes = {
  match: PropTypes.object
};

export default Edit;
